import Breadcrumbs from "@/components/design-system/breadcrumbs";
import { Button } from "@/components/design-system/button";
import { Input } from "@/components/design-system/input";
import SelectWithCheckbox from "@/components/design-system/SelectWithCheckbox";
import { getGradesDropdownService } from "@/service/base.service";
import {
  createSchool,
  fetchSchoolById,
  selectSchoolActionLoading,
  updateSchool,
} from "@/store/slices/schoolSlice";
import { SchoolModel } from "@/types/school.model";
import { successToast } from "@/utils/constants";
import { AppDispatch } from "@store";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useParams } from "react-router-dom";

interface AddSchoolProps {
  readonly viewMode?: boolean;
  readonly editMode?: boolean;
}

interface SchoolFormData {
  schoolName: string;
  address: string;
  city: string;
  district: string;
  state: string;
  pincode: string;
  adminName: string;
  title: string;
  emailAddress: string;
  contactNumber: string;
  latitude: string;
  longitude: string;
  course: string[];
  teacherLicenceCount: number | undefined;
  studentLicenceCount: number | undefined;
}

// Field configuration for reusability and scalability
interface FieldConfig {
  id: keyof SchoolFormData;
  label: string;
  placeholder: string;
  type?: string;
  required?: boolean;
  maxLength?: number;
  validationProps?: any; // Additional props for specific field types (e.g., number validation)
}

const schoolDetailsFields: FieldConfig[] = [
  {
    id: "schoolName",
    label: "School Name",
    placeholder: "Enter School Name",
    required: true,
    maxLength: 60,
  },
  {
    id: "address",
    label: "Address",
    placeholder: "Enter Address",
    required: false,
  },
  {
    id: "city",
    label: "City",
    placeholder: "Enter Your City",
    required: false,
  },
  {
    id: "district",
    label: "District",
    placeholder: "Enter Your District",
    required: false,
  },
  {
    id: "state",
    label: "State",
    placeholder: "Enter Your State",
    required: false,
  },
  {
    id: "pincode",
    label: "Pincode",
    placeholder: "Enter Your Pincode",
    required: false,
  },
  {
    id: "latitude",
    label: "Latitude",
    placeholder: "Enter Your Latitude",
    required: true,
  },
  {
    id: "longitude",
    label: "Longitude",
    placeholder: "Enter Your Longitude",
    required: true,
  },
];

const adminDetailsFields: FieldConfig[] = [
  {
    id: "adminName",
    label: "School Admin Name",
    placeholder: "Enter Admin Name",
    required: true,
    maxLength: 60,
  },
  {
    id: "title",
    label: "Title",
    placeholder: "Enter Title",
    required: true,
    maxLength: 60,
  },
  {
    id: "emailAddress",
    label: "Email Address",
    placeholder: "Enter Email Address",
    type: "email",
    required: true,
  },
  {
    id: "contactNumber",
    label: "Contact Number",
    placeholder: "Enter Contact Number",
    type: "tel",
    required: true,
  },
];
const validationProps = {
  className: "no-spinner",
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!/^[0-9]$/.test(e.key) && e.key !== "Backspace") {
      e.preventDefault();
    }
  },
  onWheel: (e: React.WheelEvent<HTMLInputElement>) => {
    e.currentTarget.blur();
  },
}
const licenseCountFields: FieldConfig[] = [
  {
    id: "teacherLicenceCount",
    label: "Teacher License Count",
    placeholder: "Enter Count",
    type: "number",
    required: true,
    validationProps
  },
  {
    id: "studentLicenceCount",
    label: "Student License Count",
    placeholder: "Enter Count",
    type: "number",
    required: true,
    validationProps
  }
];

export default function AddSchool({
  viewMode = false,
  editMode = false,
}: AddSchoolProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const actionLoading = useSelector(selectSchoolActionLoading);
  const { id } = useParams();
  const [schoolData, setSchoolData] = useState<SchoolModel | null>(null);
  const [isLoading, setIsLoading] = useState(viewMode || editMode);
  const [grades, setGrades] = useState<{ id: string; grade: string }[]>([]);

  const initialFetchDone = useRef(false);

  useEffect(() => {
    // Only fetch if we haven't done the initial fetch yet
    if (!initialFetchDone.current) {
      const fetchGrades = async () => {
        try {
          const { data } = await getGradesDropdownService({
            search: "",
          });
          setGrades(data.data);
        } catch (error) {
          console.error("Error fetching clients:", error);
        }
      };

      fetchGrades();

      // Mark that we've done the initial fetch
      initialFetchDone.current = true;
    }
  }, [dispatch, viewMode]);

  // Fetch school data when in view or edit mode
  useEffect(() => {
    if ((viewMode || editMode) && id) {
      setIsLoading(true);
      dispatch(fetchSchoolById(id))
        .unwrap()
        .then((data) => {
          setSchoolData(data);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching school:", error);
          // errorToast("Error", "Failed to load school details");
          setIsLoading(false);
          navigate("/school-management");
        });
    }
  }, [viewMode, editMode, id, dispatch, navigate]);

  // Initialize react-hook-form
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<SchoolFormData>({
    defaultValues: {
      schoolName: "",
      address: "",
      city: "",
      district: "",
      state: "",
      pincode: "",
      adminName: "",
      title: "",
      emailAddress: "",
      contactNumber: "",
      course: [],
      latitude: "",
      longitude: "",
    },
    mode: "onBlur",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
  });

  // Set form values when school data is loaded in view or edit mode
  useEffect(() => {
    if ((viewMode || editMode) && schoolData) {
      setValue("schoolName", schoolData.name);
      setValue("address", schoolData.location?.address || "");
      // City is stored as part of address in the API
      setValue("city", schoolData.location?.city || "");
      setValue("district", schoolData.location?.district || "");
      setValue("state", schoolData.location?.state || "");
      setValue("pincode", schoolData.location?.pincode || "");
      setValue("latitude", schoolData.location?.latitude || "");
      setValue("longitude", schoolData.location?.longitude || "");

      setValue("adminName", schoolData.user.name);
      setValue("title", schoolData.user.title);
      setValue("emailAddress", schoolData.user.email);
      setValue("contactNumber", schoolData.user.contact_number);
      setValue("studentLicenceCount", schoolData.no_of_student_licence);
      setValue("teacherLicenceCount", schoolData.no_of_teacher_licence);

      // Handle course data which could be array of strings or objects
      const courses = Array.isArray(schoolData.user.course)
        ? schoolData.user.course.map((course) =>
          typeof course === "string" ? course : course?.id || ""
        )
        : [];
      setValue("course", courses);
    }
  }, [schoolData, setValue, viewMode, editMode]);

  // Form submission handler
  const onFormSubmit = async (data: SchoolFormData) => {
    try {
      // Transform form data to match the SchoolModel interface
      const schoolData: Partial<SchoolModel> = {
        name: data.schoolName,
        location: {
          address: data.address,
          district: data.district,
          state: data.state,
          pincode: data.pincode,
          city: data.city,
          latitude: data.latitude,
          longitude: data.longitude,
        },
        user: {
          name: data.adminName,
          title: data.title,
          email: data.emailAddress,
          contact_number: data.contactNumber,
          course: data.course,
        } as any,
        no_of_student_licence: data.studentLicenceCount,
        no_of_teacher_licence: data.teacherLicenceCount,
        isActive: true,
      };

      if (editMode && id) {
        // Update existing school
        const resultAction = await dispatch(updateSchool({ id, schoolData }));

        if (updateSchool.fulfilled.match(resultAction)) {
          // Show success toast
          successToast(`School Updated Successfully.`, "");

          navigate(-1);
        }
      } else {
        // Create new school
        const resultAction = await dispatch(
          createSchool(schoolData as SchoolModel)
        );

        if (createSchool.fulfilled.match(resultAction)) {
          // Show success toast
          successToast(`School Added Successfully.`, "");

          navigate("/school-management");
        }
      }
    } catch (error) {
      console.error("Error saving school:", error);
    }
  };

  // Form validation rules
  const getValidationRules = (field: FieldConfig) => {
    const rules: Record<string, unknown> = {};

    if (field.required) {
      switch (field.id) {
        case "schoolName":
          rules.required = "Please enter the school name.";
          break;
        case "adminName":
          rules.required = "Please enter the admin name.";
          break;
        case "title":
          rules.required = "Please enter the title.";
          break;
        case "emailAddress":
          rules.required = "Please enter a valid email address.";
          break;
        case "contactNumber":
          rules.required = "Please enter a valid mobile number.";
          break;
        case "latitude":
          rules.required = "Please enter a valid latitude.";
          break;
        case "longitude":
          rules.required = "Please enter a valid longitude.";
          break;
        default:
          rules.required = `${field.label} is required`;
      }
    }

    if (field.id === "schoolName" || field.id === "adminName") {
      rules.pattern = {
        // Allows alphanumeric characters but requires at least one letter
        value: /^(?=.*[a-zA-Z])[a-zA-Z0-9\s]+$/,
        message:
          field.id === "schoolName"
            ? "School name must contain letters and can include numbers"
            : "Admin name must contain letters and can include numbers",
      };
    }

    if (field.id === "latitude") {
      rules.pattern = {
        // Allows numbers with optional decimal point and negative sign
        // Valid formats: 12, -12, 12.345, -12.345
        value: /^-?([0-9]*[.])?[0-9]+$/,
        message:
          "Latitude must contain only numbers, decimal point, and optional negative sign",
      };
      rules.validate = {
        validRange: (value: string) => {
          const num = parseFloat(value);
          return (
            (num >= -90 && num <= 90) ||
            "Latitude must be between -90 and 90 degrees"
          );
        },
      };
    }

    if (field.id === "longitude") {
      rules.pattern = {
        // Allows numbers with optional decimal point and negative sign
        // Valid formats: 12, -12, 12.345, -12.345
        value: /^-?([0-9]*[.])?[0-9]+$/,
        message:
          "Longitude must contain only numbers, decimal point, and optional negative sign",
      };
      rules.validate = {
        validRange: (value: string) => {
          const num = parseFloat(value);
          return (
            (num >= -180 && num <= 180) ||
            "Longitude must be between -180 and 180 degrees"
          );
        },
      };
    }

    if (field.id === "emailAddress") {
      // Custom validation for email to handle spaces and double @ symbols
      rules.validate = {
        validFormat: (value: string) => {
          // More comprehensive email validation
          // - Validates local part and domain format
          // - Ensures domain has at least one dot
          const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
          if (!emailRegex.test(value)) {
            return "Please enter a valid email address";
          }

          // Additional check for double @ symbols
          if ((value.match(/@/g) || []).length !== 1) {
            return "Email address should contain exactly one @ symbol";
          }

          return true;
        },
      };
    }

    if (field.id === "contactNumber") {
      rules.pattern = {
        value: /^[0-9]{10}$/,
        message: "Please enter a valid mobile number",
      };
    }

    return rules;
  };

  const handleCancel = () => {
    navigate("/school-management");
  };

  // Reusable field renderer for consistent UI
  const renderField = (field: FieldConfig) => (
    <div key={field.id} className="w-full">
      <Input
        id={field.id}
        {...register(field.id, getValidationRules(field))}
        label={field.label}
        type={field.type ?? "text"}
        placeholder={field.placeholder}
        required={field.required}
        maxLength={field.maxLength || undefined}
        errorMessage={errors[field.id]?.message as string}
        disabled={viewMode || (editMode && field.id === "emailAddress")}
        readOnly={viewMode || (editMode && field.id === "emailAddress")}
        {...field.validationProps}
      />
    </div>
  );

  return (
    <div className="h-full flex flex-col gap-6">
      {isLoading ? (
        <div className="bg-white rounded-[12px] h-[calc(100vh-120px)] flex items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-t-[#5C88C5] border-r-[#5C88C5] border-b-[#5C88C5] border-l-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-600">Loading school details...</p>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-[12px] h-full">
          <form
            onSubmit={handleSubmit(onFormSubmit)}
            className="w-full h-full flex flex-col"
          >
            {/* School Details Section */}
            <div className={`px-[32px] py-[33px] flex flex-col flex-1`}>
              <div className="mb-8">
                <div className="">
                  <Breadcrumbs
                    title={
                      viewMode
                        ? "School Details"
                        : editMode
                          ? "Edit School"
                          : "Add School"
                    }
                    onClick={() => navigate("/school-management")}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {schoolDetailsFields.map(renderField)}
                </div>
              </div>

              <div className="w-full h-px bg-[#E2E8F0] mt-[48px] mb-[32px]"></div>

              {/* School Admin Details Section */}
              <div className="mb-8">
                <h2 className="text-[#1F1F1F] text-[20px] font-bold mb-6">
                  School Admin Details
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {adminDetailsFields.map(renderField)}

                  {/* Course Selection */}

                  <div className="w-full">
                    <SelectWithCheckbox
                      id="course"
                      label="Course"
                      placeholder="Select a course"
                      options={grades.map((grade) => ({
                        value: grade.id,
                        label: grade.grade,
                      }))}
                      selectedText="Courses"
                      showAbove={false}
                      value={watch("course")}
                      onChange={(value) =>
                        setValue("course", value as string[], {
                          shouldValidate: true,
                        })
                      }
                      required={true}
                      disabled={viewMode}
                      error={errors.course?.message as string}
                    />

                    <input
                      type="hidden"
                      {...register("course", {
                        required: !viewMode ? "Course is required" : false,
                      })}
                    />
                  </div>
                </div>
              </div>
              {/* License Count Section */}
              <div className="mb-8">
                <h2 className="text-[#1F1F1F] text-[20px] font-bold mb-6">
                  License Count
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {licenseCountFields.map(renderField)}
                </div>
              </div>
            </div>
            {/* Form Actions - Only show in add or edit mode */}
            <div className="flex h-[98px] px-[32px] justify-end space-x-4 border-t border-gray-200 pt-6 shadow-[0px_4px_24px_8px_#0000001F]">
              {viewMode ? (
                <Button
                  type="button"
                  className="h-[48px] w-[161px] rounded-[8px] bg-[#5C88C5]"
                  onClick={handleCancel}
                >
                  Close
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    className="h-[48px] w-[161px]"
                    variant="primary-outline"
                    onClick={handleCancel}
                    disabled={actionLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="h-[48px] w-[161px] rounded-[8px] bg-[#5C88C5]"
                    variant="primary"
                    disabled={!isValid || actionLoading}
                    loading={actionLoading}
                  >
                    {editMode ? "Update" : "Add"}
                  </Button>
                </>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
